/**
 * The destinations a staged unit can land on: the production itself ("No site")
 * plus every site under it. Units are dropped here by drag, or moved in bulk by
 * clicking a box. A box with contents is itself draggable, so a whole group can
 * be poured into another destination.
 */

import { useState } from 'react';
import { Download, HelpCircle } from 'lucide-react';
import { PARENT_GROUP_TARGET, type BulkAssignGroup } from './types';
import { PARENT_GROUP_COLOR, siteColorAt, tint } from './targetStyles';

interface DropTargetsProps {
  parentGroupName: string;
  sites: BulkAssignGroup[];
  countFor: (target: string) => number;
  idsIn: (target: string) => string[];
  /** Fires when units are dropped on, or the box is clicked to collect them. */
  onDropUnits: (unitIds: string[], target: string) => void;
  onDragUnits: (unitIds: string[]) => void;
  onDragEnd: () => void;
  /** Every ticked unit, moved in when a box is clicked. */
  allStagedIds: string[];
  isDragging: boolean;
}

export function DropTargets({
  parentGroupName,
  sites,
  countFor,
  idsIn,
  onDropUnits,
  onDragUnits,
  onDragEnd,
  allStagedIds,
  isDragging,
}: DropTargetsProps) {
  const [overKey, setOverKey] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const targets = [
    { key: PARENT_GROUP_TARGET, name: 'No site', color: PARENT_GROUP_COLOR },
    ...sites.map((site, i) => ({ key: site.id, name: site.name, color: siteColorAt(i) })),
  ];

  return (
    <div className="sticky -top-5 z-[3] flex flex-col gap-2 border-b border-border bg-card py-2.5">
      <button
        type="button"
        onClick={() => setHelpOpen((v) => !v)}
        title="How assigning works"
        className="-mx-1.5 -my-1 flex items-center gap-1.5 self-start rounded-md px-1.5 py-1 transition-colors hover:bg-accent"
      >
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          To assign a unit, drag units onto a production/site below
        </span>
        <HelpCircle
          className={`h-4 w-4 ${helpOpen ? 'text-[#8FB560]' : 'text-muted-foreground'}`}
        />
      </button>

      {helpOpen && (
        <div className="flex flex-col gap-1.5 rounded-md border border-border bg-background px-3 py-2.5">
          <span className="text-[11px] text-muted-foreground">
            Drag a unit row, or a whole model group by its handle, onto one of the boxes.
          </span>
          <span className="text-[11px] text-muted-foreground">
            Click a box to move every ticked unit into it. Drag a filled box to move its
            contents somewhere else.
          </span>
          <span className="text-[11px] text-muted-foreground">
            Each box has its own colour — units in the list carry that colour once they land.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {targets.map((target) => {
          const ids = idsIn(target.key);
          const count = countFor(target.key);
          const over = overKey === target.key;
          const filled = count > 0;
          const collectAll = () => {
            const moving = allStagedIds.filter((id) => !ids.includes(id));
            if (moving.length) onDropUnits(moving, target.key);
          };

          return (
            <div
              key={target.key}
              draggable={filled}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', ids.join(','));
                e.dataTransfer.effectAllowed = 'move';
                onDragUnits(ids);
              }}
              onDragEnd={() => {
                setOverKey(null);
                onDragEnd();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (overKey !== target.key) setOverKey(target.key);
              }}
              onDragLeave={() => setOverKey((k) => (k === target.key ? null : k))}
              onDrop={(e) => {
                e.preventDefault();
                setOverKey(null);
                const raw = e.dataTransfer.getData('text/plain');
                const dropped = raw.split(',').filter(Boolean);
                if (dropped.length) onDropUnits(dropped, target.key);
                onDragEnd();
              }}
              onClick={collectAll}
              onKeyDown={(e) => {
                // Drag-and-drop has no keyboard equivalent, so the click path
                // is the only way in — it has to be reachable.
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  collectAll();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Move every ticked unit into ${target.name}. ${count} staged.`}
              title="Drop units here, or click to move every ticked unit in"
              className="flex h-[58px] min-w-0 flex-row items-center gap-2.5 rounded-lg px-2.5 text-left transition-colors hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{
                flex: '0 0 calc((100% - 8px) / 2)',
                border: over
                  ? `2px solid ${target.color}`
                  : filled
                    ? `1px solid ${tint(target.color, 0.6)}`
                    : `1px dashed ${tint(target.color, isDragging ? 0.9 : 0.55)}`,
                background: tint(
                  target.color,
                  over ? 0.24 : isDragging ? 0.14 : filled ? 0.12 : 0.05
                ),
                cursor: filled ? 'grab' : 'pointer',
              }}
            >
              <span
                className="flex h-7 w-7 flex-none items-center justify-center rounded-[7px] transition-colors"
                style={{
                  color: target.color,
                  background: tint(target.color, over ? 0.34 : isDragging ? 0.26 : 0.16),
                  border: `1px solid ${tint(target.color, over ? 0.7 : 0.4)}`,
                }}
              >
                <Download className="h-[17px] w-[17px]" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-px text-left">
                <span
                  className="w-full truncate text-[9px] font-medium uppercase tracking-wider"
                  style={{ color: tint(target.color, 0.9) }}
                >
                  {parentGroupName}
                </span>
                <span className="w-full truncate text-xs leading-tight text-foreground">
                  {target.name}
                </span>
              </span>
              <span
                className="flex-none text-[17px] font-semibold tabular-nums"
                style={{ color: target.color }}
              >
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
